import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useSpacePrices } from '@cocoonspace/shared/hooks/cocoon-api'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { ArrowLeftIcon, CalendarIcon, Users, X } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import { useMemo, type ComponentProps, type PropsWithChildren } from 'react'
import { PageLoader } from '~/components/page-loader'
import { Price } from '~/components/price'
import { Button } from '~/components/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '~/components/ui/select'
import { Separator } from '~/components/ui/separator'
import { SpacePricesCalendar } from '~/domains/spaces/components/space-prices-calendar'
import { cn } from '~/lib/utils'
import { DeskBookingSummaryAvailabilities } from './desk-booking-summary'
import { useDeskBookingStore } from './desk-booking.store'

const CalendarDatePicker = ({
	space,
	selected,
	onSelect,
}: {
	space: Space
	selected: Date
	onSelect: (date?: Date) => void
}) => {
	const fromDate = dateTimeUtils(space.desk_bookable_after)

	const maxDate = dateTimeUtils().add(
		space.desk_type === 'cocoon' ? 1 : 8,
		'weeks',
	)

	const spacePricesQuery = useSpacePrices(space.id, {
		from: dateTimeUtils().customFormat('dateTech'),
		to: dateTimeUtils().add(2, 'months').customFormat('dateTech'),
		// @ts-ignore
		type: 'desk',
	})

	const disabled = useMemo(() => {
		const disabledDays = []

		if (space.desk_type === 'cocoon') {
			disabledDays.push({ dayOfWeek: [0, 6] })
		}

		const closedDays = spacePricesQuery.data
			?.filter(({ closed, available }) => closed || available === false)
			.map(({ day }) => dateTimeUtils(day).toDate())

		if (closedDays?.length) {
			disabledDays.push(...closedDays)
		}

		return disabledDays
	}, [spacePricesQuery.data])

	const numberOfMonths = fromDate.isSame(maxDate, 'month') ? 1 : 2

	if (spacePricesQuery.isLoading) return <PageLoader className='h-[200px]' />

	return (
		<SpacePricesCalendar
			mode='single'
			spaceId={space.id}
			numberOfMonths={numberOfMonths}
			fromDate={
				fromDate.isAfter(dateTimeUtils())
					? fromDate.toDate()
					: dateTimeUtils().toDate()
			}
			maxDate={maxDate.format('YYYY-MM-DD')}
			components={{ DayContent: undefined }}
			disabled={disabled}
			selected={selected}
			onSelect={onSelect}
		/>
	)
}

const PeopleSelect = ({
	value,
	onValueChange,
}: ComponentProps<typeof Select>) => {
	const { t } = useTranslation()

	return (
		<Select
			value={value}
			onValueChange={onValueChange}
		>
			<SelectTrigger className='[&>span]:w-full [&>span]:text-left'>
				<Users
					size={18}
					className='text-primary'
				/>

				<SelectValue />
			</SelectTrigger>

			<SelectContent>
				{Array.from({ length: 4 }).map((_, i) => (
					<SelectItem
						key={i}
						value={(i + 1).toString()}
					>
						{t('space:sidebarDesk.person', { count: i + 1 })}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

const StepperHeader = ({
	children,
	onClickPrev,
}: PropsWithChildren<{ onClickPrev?: VoidFunction }>) => (
	<div className='flex w-full flex-row items-center rounded-t bg-white p-4 pb-0'>
		<button
			type='button'
			onClick={onClickPrev}
		>
			<ArrowLeftIcon size={26} />
		</button>

		<div className='flex-1 pr-6 text-center font-bold text-lg'>{children}</div>
	</div>
)

export const StepStart = ({
	space,
}: {
	space: Space
}) => {
	const { t } = useTranslation()

	const formData = useDeskBookingStore.use.formData()
	const setFormData = useDeskBookingStore.use.setFormData()
	const setSidebarStep = useDeskBookingStore.use.setSidebarStep()

	return (
		<div className='flex w-full flex-1 flex-col items-center divide-y'>
			<div className='flex h-[60px] w-full flex-row items-center justify-between gap-1 p-4 px-6 text-xl leading-none'>
				<div className='mb-0.5 text-sm'>{t('common:from')}</div>

				<Price
					price={space.desk_price}
					suffix='dailyLong'
					suffixClassName='text-xs font-normal mb-0.5'
					taxClassName='mb-0.5'
					taxType='ht'
				/>
			</div>

			<div className='flex w-full flex-col gap-6 p-6'>
				<div className='flex h-[40px] flex-row items-center rounded border px-4'>
					<button
						type='button'
						className={cn(
							'flex flex-1 flex-row items-center gap-3 py-2 text-sm capitalize',
							{ 'text-foreground/50': !formData.day },
						)}
						onClick={() => setSidebarStep('date')}
					>
						<CalendarIcon
							size={18}
							className='text-primary'
						/>

						{formData.day
							? dateTimeUtils(formData.day).customFormat('dateFull')
							: t('space:sidebarDesk.yourSlot')}
					</button>

					{!!formData.day && (
						<button
							type='button'
							className='ml-auto'
							onClick={() => setFormData({ day: '' })}
						>
							<X className='size-4 text-primary' />
						</button>
					)}
				</div>

				<Button
					variant='gradient'
					className='w-full'
					onClick={() => setSidebarStep('date')}
				>
					{t('space:sidebarDesk.selectTimeSlot')}
				</Button>
			</div>
		</div>
	)
}

export const StepDate = ({
	space,
}: {
	space: Space
}) => {
	const { t } = useTranslation()

	const formData = useDeskBookingStore.use.formData()
	const setFormData = useDeskBookingStore.use.setFormData()
	const setSidebarStep = useDeskBookingStore.use.setSidebarStep()

	return (
		<div className='relative flex max-h-[512px] flex-col overflow-hidden'>
			<StepperHeader onClickPrev={() => setSidebarStep('start')}>
				{t('space:sidebarDesk.date')}
			</StepperHeader>

			<div className='flex-1 overflow-hidden overflow-y-visible'>
				<CalendarDatePicker
					space={space}
					selected={new Date(formData.day)}
					onSelect={(day) => {
						if (!day) return

						setFormData({ day: dateTimeUtils(day).customFormat('dateTech') })
						setSidebarStep('nbPeople')
					}}
				/>
			</div>
		</div>
	)
}

export const PriceRow = (props: ComponentProps<'div'>) => (
	<div
		{...props}
		className='flex w-full flex-row items-center justify-between'
	/>
)

export const StepNbPeople = ({
	space,
}: {
	space: Space
}) => {
	const { t } = useTranslation()

	const formData = useDeskBookingStore.use.formData()
	const setFormData = useDeskBookingStore.use.setFormData()
	const setSidebarStep = useDeskBookingStore.use.setSidebarStep()

	return (
		<div className='flex w-full flex-1 flex-col items-center gap-6'>
			<StepperHeader onClickPrev={() => setSidebarStep('date')}>
				{t('space:sidebarDesk.participants')}
			</StepperHeader>

			<div className='flex w-full flex-col gap-6 px-6'>
				<div className='flex flex-col gap-2'>
					<PeopleSelect
						value={`${formData.nbPeople}`}
						onValueChange={(nbPeople) => {
							setFormData({
								nbPeople: nbPeople ? Number(nbPeople) : 1,
							})
						}}
					/>

					<div className='text-center text-foreground/70 text-sm'>
						💡 {t('space:sidebarDesk.nbPeopleHint')}
					</div>
				</div>
			</div>

			<Separator />

			<DeskBookingSummaryAvailabilities space={space} />
		</div>
	)
}
